import { useState, useCallback } from 'react';
import axios from 'axios';

export const useVisionModelFallback = () => {
  const [isChecking, setIsChecking] = useState(false);
  const [fallbackInfo, setFallbackInfo] = useState(null);

  const checkVisionFallback = useCallback(async (provider, model, hasFiles = false) => {
    // If no files, no need to check vision capability
    if (!hasFiles) {
      setFallbackInfo(null);
      return { provider, model };
    }

    setIsChecking(true);
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const response = await axios.get('/api/vision-fallback', {
        params: { provider, model },
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      const { needsFallback, fallbackModel, currentModel, message } = response.data;
      
      if (needsFallback && fallbackModel) {
        setFallbackInfo({
          originalModel: { provider, model },
          fallbackModel,
          message,
          isProviderFallback: fallbackModel.isProviderFallback
        });
        
        return {
          provider: fallbackModel.provider,
          model: fallbackModel.model,
          usingFallback: true,
          fallbackReason: message
        };
      } else if (!needsFallback && currentModel) {
        setFallbackInfo(null);
        return { provider, model, usingFallback: false };
      } else {
        // No vision models available at all
        setFallbackInfo({
          originalModel: { provider, model },
          fallbackModel: null,
          message: 'No vision-capable models are available. Please enable vision models in admin settings.',
          isProviderFallback: false
        });
        
        // Return original model but with error info
        return {
          provider,
          model,
          usingFallback: false,
          error: 'No vision models available'
        };
      }
    } catch (error) {
      console.error('Error checking vision fallback:', error);
      setFallbackInfo({
        originalModel: { provider, model },
        fallbackModel: null,
        message: 'Error checking vision capability. Using original model.',
        isProviderFallback: false
      });
      
      return { provider, model, usingFallback: false, error: error.message };
    } finally {
      setIsChecking(false);
    }
  }, []);

  const clearFallbackInfo = useCallback(() => {
    setFallbackInfo(null);
  }, []);

  return {
    checkVisionFallback,
    fallbackInfo,
    isChecking,
    clearFallbackInfo
  };
}; 